import React from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import {
  AIModelRef,
  ResolvedSuggestion,
  ObservationTypeValue,
} from "../common/ObjectTypes";
import { tailwindClassnames } from "../common/TailwindClassnames";
import ReviewCardBody from "./ReviewCardBody";
import { suggestionOwnerKey } from "./mediaDetailComposer";

/**
 * Human-readable label for an observation type. Values come from the API enum;
 * the display strings are translated.
 */
export const observationTypeLabel = (
  t: (key: string) => string,
  type: ObservationTypeValue,
): string => {
  switch (type) {
    case "animal":
      return t("Animal");
    case "human":
      return t("Human");
    case "vehicle":
      return t("Vehicle");
    case "blank":
      return t("Blank");
    default:
      return t("Unknown");
  }
};

const pct = (value: number): string => `${Math.round(value * 100)}%`;

/**
 * Join model names for the provenance line, in the active locale.
 *
 * `Intl.ListFormat` rather than `.join(", ")` because list joining is
 * locale-specific and this app ships en / es / fr / de — French and German do
 * not follow the English pattern. `type: "unit"` gives a comma-separated list
 * ("SpeciesNet, DeepFaune"), which is the approved copy; `"conjunction"` would
 * add "and" and change a signed-off string.
 *
 * The `typeof` guard is a defensive fallback for a runtime without
 * `Intl.ListFormat`, not the intended path — every browser this app supports
 * has it, as does the Node 20 that runs the suite. It exists so a missing Intl
 * cannot throw during render and blank the whole card over one line of text.
 * @param language active i18n language code
 * @param names model names, in the order the API gave them
 * @returns the joined list
 */
const joinModelNames = (language: string, names: string[]): string =>
  typeof Intl.ListFormat === "function"
    ? new Intl.ListFormat(language, { style: "long", type: "unit" }).format(
        names,
      )
    : names.join(", ");

/**
 * The provenance line for a suggestion card (AKL-780): which models contributed
 * to it, and whether they agreed. Selected purely from the two fields AKL-775's
 * API emits — agreement is never re-derived here.
 *
 * Four branches:
 * - no models (absent or empty) → `null`, so nothing renders. A legacy
 *   prediction whose run has no resolvable model must show no line at all
 *   rather than an empty label.
 * - exactly one → the existing `Model: {{name}}` key, shared with
 *   `PredictionPopover`. **Length is tested before `modelsAgree`** on purpose:
 *   the API reports agreement for a single-model card (a model trivially agrees
 *   with itself) and "Models agree: SpeciesNet" would be nonsense.
 * - two or more that agree → `Models agree: {{names}}`.
 * - two or more that do not → `Models: {{names}}`, which covers both "they
 *   contributed unevenly across the card" and "they disagreed somewhere".
 *
 * `AIModelRef.version` is deliberately unused: the approved copy interpolates
 * the name only, and telling two versions of one model apart is AKL-776's
 * problem (precedence), not this line's.
 * @param t translator
 * @param language active i18n language code, for locale-correct list joining
 * @param models every model that contributed to the card
 * @param modelsAgree the card's `models_agree` flag
 * @returns the line to render, or null when there is nothing to say
 */
export const modelProvenanceLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  language: string,
  models: AIModelRef[] | undefined,
  modelsAgree: boolean | undefined,
): string | null => {
  if (!models?.length) return null;
  if (models.length === 1) {
    return t("Model: {{name}}", { name: models[0].name });
  }
  const names = joinModelNames(
    language,
    models.map((m) => m.name),
  );
  return modelsAgree
    ? t("Models agree: {{names}}", { names })
    : t("Models: {{names}}", { names });
};

/** Current committed state of the cluster a card represents (§6.2.2). */
export type SuggestionReviewState = "accepted" | "rejected" | "edited" | null;

type SuggestionCardProps = {
  suggestion: ResolvedSuggestion;
  /** Committed review state of the cluster, derived by the surface. */
  reviewState?: SuggestionReviewState;
  /** Card-level commit handlers (AKL-682). Omit for a read-only card. */
  onAccept?: () => void;
  onReject?: () => void;
  onEdit?: () => void;
  /** Disables the buttons while an action is committing. */
  busy?: boolean;
  /** Which of this card's buttons is mid-commit, so it shows a spinner. */
  pending?: "accept" | "reject" | null;
  /** Highlights the card as the current keyboard-focused card (§6.2.6). */
  focused?: boolean;
  /** Fired when focus enters the card (any inner control) — sets keyboard focus. */
  onFocusCard?: () => void;
  /** Fired when focus leaves the card for a non-card element — clears keyboard focus. */
  onBlurCard?: () => void;
  /**
   * AKL-698: pointer-selected state, distinct from keyboard `focused`. The
   * surface sets this when the reviewer clicks the card to persist its
   * card ↔ bbox highlight. Both visuals can stack.
   */
  selected?: boolean;
  /**
   * AKL-698: fired on pointer enter / leave. Receives the card's aggregation
   * key on enter and `null` on leave, so the surface can drive
   * `highlightedBoxIds` on the image overlay.
   */
  onCardHover?: (aggregationKey: string | null) => void;
  /**
   * AKL-698: fired on pointer / keyboard activation of the card body (not the
   * inner buttons). The surface uses this to toggle pointer selection.
   */
  onCardClick?: (aggregationKey: string) => void;
  /**
   * AKL-774: the card is tickable because the surface is in assign-species
   * mode. Puts a checkbox on the card and makes a body click tick it rather
   * than toggle the AKL-698 single-card highlight.
   */
  selectable?: boolean;
  /** AKL-774: whether this card is currently ticked. */
  checked?: boolean;
  /** AKL-774: fired when the card is ticked or unticked. */
  onToggleSelect?: (aggregationKey: string) => void;
};

/**
 * Aggregated suggestion card (§6.2.3), styled after the Phase 2 review-panel
 * mockup: an "AI suggested" badge with the source detection count, the resolved
 * taxon (species), and the individual count. When commit handlers are supplied
 * (AKL-682) it renders Accept / Reject / Edit and reflects the cluster's
 * committed review state; with no handlers it stays read-only.
 *
 * AKL-698: cards in the `"rejected"` review state get the stronger
 * greyed / strikethrough / red-bar treatment and hide their Reject button so
 * the inline Accept / Edit act as the one-click "rejected by mistake" recovery
 * path. `"accepted"` and `"edited"` clusters are filtered out at the surface —
 * they render as `ObservationList` rows instead — so this component's
 * resolved-state path only covers `"rejected"`.
 *
 * AKL-780: below the count sits the model provenance line, naming every model
 * that contributed to the card and whether they agreed. It renders on a
 * rejected card too, unstruck — which models ran is as true after a rejection
 * as before it, and the recovery path should not show less than the card did.
 */
const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  reviewState = null,
  onAccept,
  onReject,
  onEdit,
  busy,
  pending,
  focused,
  onFocusCard,
  onBlurCard,
  selected,
  onCardHover,
  onCardClick,
  selectable = false,
  checked = false,
  onToggleSelect,
}) => {
  const { t, i18n } = useTranslation();
  const isRejected = reviewState === "rejected";
  const provenance = modelProvenanceLabel(
    t,
    i18n.language,
    suggestion.models,
    suggestion.models_agree,
  );
  // While the surface is picking cards, the per-card commits are hidden. Acting
  // on one card mid-selection is ambiguous - the ticked set is what the reviewer
  // is working with - and the buttons would also compete with the body click
  // that now ticks the card.
  const selecting = selectable;
  // Reject action is hidden on already-rejected cards: inline Accept / Edit
  // are the one-click recovery affordances. Card actions still render even for
  // rejected cards.
  const hasActions =
    !selecting && Boolean(onAccept || (onReject && !isRejected) || onEdit);
  const containerClasses = [
    "rounded-lg border p-3",
    isRejected
      ? `${tailwindClassnames.resolvedCardBase} ${tailwindClassnames.resolvedBarRejected} border-black/10 dark:border-white/10`
      : "bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10",
    // Focus indicator: always the ring. On a rejected card, don't also recolor
    // the border — `border-sky-blue-light` sets every edge and would wipe out
    // the red left-bar (`resolvedBarRejected`); the ring alone reads as focus.
    focused
      ? isRejected
        ? "ring-2 ring-sky-blue-light"
        : "border-sky-blue-light ring-2 ring-sky-blue-light"
      : "",
    selected || checked ? tailwindClassnames.cardPointerSelected : "",
    onCardClick || selecting ? "cursor-pointer" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const activate = () => {
    if (selecting) {
      onToggleSelect?.(suggestionOwnerKey(suggestion));
      return;
    }
    onCardClick?.(suggestionOwnerKey(suggestion));
  };
  // Only bound when the container is itself a control. In selection mode the
  // checkbox is the control and handles its own keyboard, so the container is
  // left inert rather than being a focusable element with no role.
  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onCardClick || selecting) return;
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  };
  return (
    <div
      className={containerClasses}
      // In selection mode the checkbox inside is the real control: it owns the
      // role, the checked state and the keyboard. The container keeps only its
      // pointer shortcut (click anywhere on the card to tick it) and is
      // deliberately NOT focusable — a focusable element that handles Enter and
      // Space but has no role is worse for assistive tech than no control at
      // all, and giving it one would announce the card twice.
      role={!selecting && onCardClick ? "button" : undefined}
      tabIndex={!selecting && onCardClick ? 0 : undefined}
      aria-pressed={!selecting && onCardClick ? Boolean(selected) : undefined}
      onFocus={onFocusCard}
      onBlur={(e) => {
        // Clear keyboard focus only when focus leaves the suggestion cards
        // entirely — moving between cards (or between controls inside a card)
        // keeps a card focused. Prevents A/R/E acting on a stale card once
        // focus has moved to Save / Mark as Blank etc.
        const next = e.relatedTarget as HTMLElement | null;
        if (!next?.closest?.('[data-testid="suggestion-card"]')) {
          onBlurCard?.();
        }
      }}
      onMouseEnter={() => onCardHover?.(suggestionOwnerKey(suggestion))}
      onMouseLeave={() => onCardHover?.(null)}
      onClick={(e) => {
        if (!onCardClick && !selecting) return;
        // Ignore clicks that originated on an inner interactive control —
        // Accept / Reject / Edit fire their own handlers and shouldn't also
        // toggle card selection. The checkbox is excluded too: it fires its own
        // onChange, and letting the body handler run as well would tick and
        // immediately untick it.
        const target = e.target as HTMLElement | null;
        if (target?.closest?.("button, a, input, textarea, select")) return;
        activate();
      }}
      onKeyDown={handleCardKeyDown}
      data-testid="suggestion-card"
      data-selected={selected ? "true" : undefined}
      data-checked={checked ? "true" : undefined}
    >
      <ReviewCardBody
        header={
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {selecting && (
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 accent-sky-blue focus-visible:ring-2 focus-visible:ring-sky-blue-light"
                checked={checked}
                onChange={() =>
                  onToggleSelect?.(suggestionOwnerKey(suggestion))
                }
                aria-label={suggestion.display_name}
                data-testid="suggestion-select"
              />
            )}
            <span className="rounded bg-sky-blue-light/40 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-storm-blue dark:bg-sky-blue-light/20 dark:text-sky-blue-light">
              {t("AI suggested")}
            </span>
            {isRejected && (
              <span
                className={tailwindClassnames.resolvedChipRejected}
                data-testid="suggestion-card-state"
              >
                {t("rejected")}
              </span>
            )}
            <span className="ml-auto text-xs text-off-black-light/70 dark:text-primary-off-white/50">
              {pct(suggestion.confidence_max)}
            </span>
          </div>
        }
        speciesName={suggestion.display_name}
        scientificName={suggestion.scientific_name}
        speciesTrailing={
          <span className="shrink-0 text-xs text-off-black-light/60 dark:text-primary-off-white/40">
            {observationTypeLabel(t, suggestion.observation_type)}
          </span>
        }
        count={suggestion.count}
        strikethrough={isRejected}
        footer={
          provenance && (
            // Wraps rather than truncating: the point of the line is naming the
            // models, so hiding half of them behind an ellipsis defeats it.
            // Model names are short and few, so a second line is the honest
            // behaviour in this 400px panel.
            <div
              className="mt-2 text-[11px] leading-snug text-off-black-light/60 dark:text-primary-off-white/50"
              data-testid="suggestion-models"
            >
              {provenance}
            </div>
          )
        }
        actions={
          hasActions ? (
            <div className="mt-3 flex gap-2">
              {onAccept && (
                <button
                  type="button"
                  className={`relative flex-1 rounded bg-green-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-green-600 focus-visible:ring-2 focus-visible:ring-green-400 focus:outline-none ${
                    pending === "accept" ? "" : "disabled:opacity-40"
                  }`}
                  onClick={onAccept}
                  disabled={busy}
                  data-testid="suggestion-accept"
                >
                  {/* Hide (but keep) the label while spinning so the button width
                  is stable; white spinner reads on the green fill. */}
                  <span
                    className={pending === "accept" ? "invisible" : undefined}
                  >
                    {t("Accept")}
                  </span>
                  {pending === "accept" && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <BeatLoader
                        color="#FFFFFF"
                        loading={true}
                        size={6}
                        margin={2}
                      />
                    </span>
                  )}
                </button>
              )}
              {onReject && !isRejected && (
                <button
                  type="button"
                  className={`relative flex-1 rounded bg-red-600/90 px-2 py-1 text-xs font-semibold text-white hover:bg-red-600 focus-visible:ring-2 focus-visible:ring-red-400 focus:outline-none ${
                    pending === "reject" ? "" : "disabled:opacity-40"
                  }`}
                  onClick={onReject}
                  disabled={busy}
                  data-testid="suggestion-reject"
                >
                  <span
                    className={pending === "reject" ? "invisible" : undefined}
                  >
                    {t("Reject")}
                  </span>
                  {pending === "reject" && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <BeatLoader
                        color="#FFFFFF"
                        loading={true}
                        size={6}
                        margin={2}
                      />
                    </span>
                  )}
                </button>
              )}
              {onEdit && (
                <button
                  type="button"
                  className="flex-1 rounded bg-black/10 px-2 py-1 text-xs font-semibold text-off-black-light hover:bg-black/20 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none disabled:opacity-40 dark:bg-white/10 dark:text-primary-off-white dark:hover:bg-white/20"
                  onClick={onEdit}
                  disabled={busy}
                  data-testid="suggestion-edit"
                >
                  {t("Edit")}
                </button>
              )}
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default SuggestionCard;
